import { EOL } from 'node:os';
import { createWriteStream } from 'node:fs';
import { PassThrough } from 'node:stream';
import { pipeline } from 'node:stream/promises';
import stripAnsi from 'strip-ansi';
import { outputDir } from '@wix/cli-fs';
import { dirname } from 'node:path';

interface DevLogStreamOptions {
  filePath: string;
}

interface InternalStream {
  write: (msg: string) => void;
  close: () => void;
  isClosed: () => boolean;
}

export interface DevLogStream {
  write: (msg: string) => void;
  release: () => void;
}

interface CacheEntry {
  stream: InternalStream;
  refCount: number;
}

const streamCache = new Map<string, CacheEntry>();

const createNoopStream = (): InternalStream => ({
  write: () => {},
  close: () => {},
  isClosed: () => true,
});

export const getDevLogStream = async (
  options: DevLogStreamOptions
): Promise<DevLogStream> => {
  const { filePath } = options;
  const entry = streamCache.get(filePath);

  if (entry && !entry.stream.isClosed()) {
    entry.refCount++;
    return {
      write: (msg: string) => entry.stream.write(msg),
      release: () => {
        entry.refCount--;
        if (entry.refCount === 0) {
          entry.stream.close();
          streamCache.delete(filePath);
        }
      },
    };
  }

  let stream: InternalStream;
  try {
    stream = await createDevLogStream(options);
  } catch {
    stream = createNoopStream();
  }
  const newEntry: CacheEntry = { stream, refCount: 1 };
  streamCache.set(filePath, newEntry);

  return {
    write: (msg: string) => newEntry.stream.write(msg),
    release: () => {
      newEntry.refCount--;
      if (newEntry.refCount === 0) {
        newEntry.stream.close();
        streamCache.delete(filePath);
      }
    },
  };
};

const createDevLogStream = async ({ filePath }: DevLogStreamOptions) => {
  await outputDir(dirname(filePath));

  const source = new PassThrough();
  const fileStream = createWriteStream(filePath, { flags: 'w' });

  let closed = false;
  const isClosed = () => closed;

  pipeline(source, fileStream).catch(() => {
    if (!closed) {
      closed = true;
    }
  });

  const write = (msg: string) => {
    if (closed) {
      return;
    }
    source.write(stripAnsi(msg).concat(EOL));
  };

  const close = () => {
    if (closed) {
      return;
    }
    closed = true;
    source.end();
  };

  return {
    write,
    close,
    isClosed,
  };
};
